import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Chevron,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionCategory from 'modules/surveys/store/models/QuestionCategory'
import type QuestionGroupConfig from 'modules/surveys/store/models/QuestionGroupConfig'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  form: SurveyForm | Survey
  questionGroupConfig: QuestionGroupConfig
  questionCategory: QuestionCategory
  canEdit: boolean
  expanded: boolean
}

const CategoryHeader = ({ form, questionCategory, canEdit, expanded }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [isTooltipOpen, toggleTooltipOpen] = useToggle(false)
  const [isOpen, toggleOpen] = useToggle(false)
  const isEditable = form.isDraft

  const editAction = {
    onClick: () =>
      navigate(
        navigation.engagement.surveys.id.questions.questionGroupConfigIdCategoriesQuestionCategoryIdEditCategory.fullPath(
          {
            id: form.attributes.id,
            question_category_id: questionCategory.attributes.id,
            question_group_config_id: questionCategory.attributes.surveys_question_group_config_id,
          }
        )
      ),
    title: i18n.t('surveys.form.question_categories.edit_button'),
  }

  const deleteAction = {
    onClick: () => {
      navigate(
        navigation.engagement.surveys.id.questions.questionGroupConfigIdCategoriesQuestionCategoryIdRemoveCategory.fullPath(
          {
            id: form.attributes.id,
            question_category_id: questionCategory.attributes.id,
            question_group_config_id: questionCategory.attributes.surveys_question_group_config_id,
          }
        )
      )
    },
    type: 'wrong',
    title: i18n.t('surveys.form.question_categories.delete_button'),
  }

  const actions = (() => [editAction, deleteAction])()

  return (
    <>
      <Box
        flexDirection="row"
        paddingLeft="s32"
        paddingX="s16"
        borderRadius={{
          all: 'abs004',
        }}
        border={{
          all: {
            style: 'solid',
            width: 's1',
            color: 'grey400',
          },
        }}
        flexGrow
        cursor="pointer"
        background="grey200"
        expanded={expanded}
      >
        <Box justifyContent="center" paddingX="s2">
          <Chevron direction={expanded ? 'down' : 'right'} type="secondary" />
        </Box>
        <Box paddingY="s8" paddingX="s12" gap="s2" flexGrow>
          <Text.Normal color="grey1000" weight="medium" size="300">
            {questionCategory.attributes.name}
          </Text.Normal>
          <Text.Normal color="grey800" weight="medium" size="200">
            {i18n.tp('surveys.form.question_categories.question_configs_count', {
              count: questionCategory.questionConfigs.length,
            })}
          </Text.Normal>
        </Box>
        {canEdit && (
          <Box
            alignItems="center"
            flexDirection="row"
            gap="s8"
            onClick={(ev) => ev.stopPropagation()}
          >
            <Tooltip.Alt
              attachment="top center"
              offset="-10px 0"
              open={isTooltipOpen}
              toggle={toggleTooltipOpen}
              label={i18n.t('surveys.form.question_categories.add_question')}
            >
              <Button.Router
                type="secondary"
                to={navigation.engagement.surveys.id.questions.questionGroupConfigIdCategoriesQuestionCategoryIdAddQuestion.fullPath(
                  {
                    id: form.attributes.id,
                    question_category_id: questionCategory.attributes.id,
                    question_group_config_id:
                      questionCategory.attributes.surveys_question_group_config_id,
                  }
                )}
                iconName="add"
              />
            </Tooltip.Alt>
            {isEditable && (
              <Popover attachment="bottom center" toggle={toggleOpen} open={isOpen}>
                <Button type="secondary" onClick={toggleOpen(true)} iconName="ellipsis" />
                <PopoverContent onClose={toggleOpen(false)} options={actions} />
              </Popover>
            )}
          </Box>
        )}
      </Box>
    </>
  )
}

export default observer(CategoryHeader)
